import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { MessageSquare, Trash2, Mail, Phone, Clock, Calendar, ArrowUpDown, Shield } from 'lucide-react';
import type { Conversation } from '../../types/conversations';
import type { ContactInfo } from '../../utils/contactInfoDetection';

interface ConversationWithContactInfo extends Conversation {
  contact_info?: ContactInfo[];
}

interface ConversationListProps {
  conversations: ConversationWithContactInfo[];
  loading: boolean;
  error: string | null;
  selectedId: string | undefined;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onDeleteAll: () => void;
}

export function ConversationList({
  conversations,
  loading,
  error,
  selectedId,
  onSelect,
  onDelete,
  onDeleteAll
}: ConversationListProps) {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-400 text-center p-4 bg-red-400/10 rounded-xl border border-red-400/20 backdrop-blur-xl text-sm md:text-base animate-fade-in">
        <Shield className="w-6 h-6 text-red-400 mx-auto mb-2" />
        An error occurred while loading the conversations
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="text-center p-6 md:p-8 bg-indigo-500/5 backdrop-blur-xl rounded-xl border border-indigo-400/10 animate-fade-in">
        <MessageSquare className="w-10 h-10 md:w-12 md:h-12 text-indigo-400 mx-auto mb-3 md:mb-4" />
        <p className="text-gray-400 text-sm md:text-base">No saved conversations yet</p>
        <p className="text-xs text-gray-500 mt-2">Conversations will appear here when users interact with your chatbot</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-5 animate-fade-in">
      <div className="flex justify-between items-center mb-3 md:mb-4">
        <div className="flex items-center gap-2">
          <div className="bg-gradient-to-r from-violet-600/20 to-cyan-500/20 text-white p-1.5 rounded-lg flex items-center justify-center">
            <Calendar className="w-4 h-4" />
          </div>
          <h2 className="text-base md:text-lg font-semibold text-white">
            {conversations.length} conversation{conversations.length > 1 ? 's' : ''}
          </h2>
        </div>
        <button
          onClick={onDeleteAll}
          className="flex items-center gap-1 md:gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-all text-xs md:text-sm shadow-sm hover:shadow-red-500/10"
        >
          <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
          <span>Delete all</span>
        </button>
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500 px-2 mb-2">
        <div className="flex items-center gap-1">
          <ArrowUpDown className="w-3 h-3" />
          <span>Latest conversations</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          <span>Time</span>
        </div>
      </div>

      <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 divide-y divide-white/10 overflow-hidden shadow-xl shadow-black/20">
        {conversations.map((conversation, index) => {
          // Calculer un délai d'animation basé sur l'index
          const animationDelay = `${index * 50}ms`;
          const conversationNumber = conversations.length - index;
          
          return (
            <div
              key={conversation.id}
              style={{ animationDelay }}
              className={`flex items-center justify-between p-3 md:p-4 transition-all duration-200 animate-fade-in-up ${
                selectedId === conversation.id 
                  ? 'bg-indigo-500/10 border-l-2 border-l-indigo-500' 
                  : 'hover:bg-white/5 border-l-2 border-l-transparent'
              }`}
            >
              <button
                onClick={() => onSelect(conversation.id)}
                className="flex-1 text-left"
              >
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="flex-shrink-0 opacity-40 text-gray-400 text-[10px] mt-0.5">
                    {conversationNumber < 100 ? conversationNumber : '99+'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDistanceToNow(new Date(conversation.created_at), {
                          addSuffix: true,
                          locale: enUS
                        })}
                      </span>
                      <span className="text-xs md:text-sm text-gray-200 line-clamp-1 font-medium">
                        {conversation.initial_message_content}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2">
                      {conversation.contact_info?.map((info: ContactInfo, index: number) => (
                        <span 
                          key={index}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gradient-to-r from-violet-600/40 to-cyan-500/40 text-white font-medium truncate max-w-[120px] md:max-w-[200px] border border-violet-500/20 shadow-sm"
                        >
                          {info.type === 'email' ? (
                            <Mail className="w-3 h-3 flex-shrink-0" />
                          ) : (
                            <Phone className="w-3 h-3 flex-shrink-0" />
                          )}
                          <span className="truncate">{info.value}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => onDelete(conversation.id)}
                className="ml-2 md:ml-4 p-1.5 md:p-2 text-gray-500 hover:text-red-400 transition-colors rounded-full hover:bg-red-500/10"
                title="Delete conversation"
              >
                <Trash2 className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}